<template>
    <main
        :class="[`ps-side-view ps-${side} patent-app-view__${selfName} pa-relative`]"
        :data-neighbor-name="neighborName"
        flex="|"
    >
        <ResizeObserver @notify="resizeHandler" />

        <CompareHeader class="pa-shrink-0" :side="side" />

        <template v-if="!isInComparePath">
            <div class="patent-app__header">
                <!--title-->
                <InfoTitle
                    :patent="patent"
                    :showPlaceholder="routeLoading"
                    :hideLegalStatus="false"
                    :legalOpenInNew="true"
                    class="patent-app__info-title"
                />
                <!--function-bar-->
                <FunctionBarCompare
                    id="toolbar"
                    :key="patentId"
                    :patentId="patentId"
                    :side="side"
                    :viewParams="viewParams"
                    class="patent-app__function-bar"
                />
            </div>

            <div class="ps-side-view__tab pa-flex pa-items-end">
                <MenuTabsScrollable
                    ref="menuTabsScrollable"
                    :currentTab="selfName"
                    :defaultTabs="visibleTabs"
                    :showTooltip="true"
                    @switchTabHanlder="switchTabHandler"
                />
                <CompareText v-if="side === 'left'" />
            </div>
        </template>

        <router-view
            :key="`${side}_${selfName}`"
            ref="viewVm"
            :class="['dual-aside-box', dualViewClassStatus]"
            :name="selfName"
            :side="side"
            :selfName="selfName"
            :neighborSide="neighborSide"
            :neighborName="neighborName"
            :patentId="patentId"
            flex-item="()"
        />

        <DensityBar v-if="hasDensity" :key="selfName" />

        <!-- 对比视图划段提示工具 -->
        <CompareWordingTips
            v-if="enabledCompareWording && isSourceInTextTab && side === 'left'"
            class="pa-absolute pa-bottom-6 pa-right-6 pa-z-[1]"
        />

        <template v-if="side === 'right'">
            <!-- 对比视图相似段落跳转工具 -->
            <CompareSimilarParaBar
                v-if="showSimilarParaBar || showWordingNotFoundBar"
                class="pa-absolute pa-bottom-6 pa-right-1/2 pa-z-[1] pa-translate-x-1/2"
            />
        </template>
    </main>
</template>

<script>
/*
     ⚠️ 此组件存在的目的仅仅是为了解析左右路由关系 ⚠️
        ⚠️ 请把此组件当成 Functional 组件使用！⚠️
                     🙏
*/
import DensityBar from 'library-@/components/DensityBar';
import _ from 'lodash';
import MenuTabsScrollable from 'library-@/components/MenuTabs/MenuTabsScrollable';
import { getFilteredCompareManifestList, comparePluginsMapByName } from '@/subview_plugins/install';
import { ResizeObserver } from 'vue-resize';
import InfoTitle from '@/components/InfoTitle';
import FunctionBarCompare from '@/views/function-bar/function-bar_compare';
import CompareHeader from '@/components/compare-components/compare-header/CompareHeader.vue';
import { getIsFreeView } from 'library-@/utils/patent-view-free-judgment-util';
import compareManifest from '@/subview_plugins/compare/manifest';
import CompareText from '@/components/compare-components/compare-text/CompareText.vue';
import CompareSimilarParaBar from '@/components/compare-components/compare-similar-para-bar/CompareSimilarParaBar.vue';
import CompareWordingTips from '@/components/compare-components/compare-wording/CompareWordingTips.vue';
import { useSimilarParaScroll } from '@/composables/use-similar-para-scroll';
import { useCompareWording } from '@/composables/use-compare-wording';
import { useCompareUrlState } from '@/composables/use-compare-url-state';
import { useDualViewStates } from '@/composables/use-dual-view-states';
import { usePermission } from '@/composables/use-permission';
import { usePatentBasic } from '@/composables/use-patent-basic';

export default {
    name: 'CompareView',
    components: {
        MenuTabsScrollable,
        DensityBar,
        ResizeObserver,
        FunctionBarCompare,
        InfoTitle,
        CompareHeader,
        CompareText,
        CompareSimilarParaBar,
        CompareWordingTips,
    },
    props: {
        patentId: {
            type: String,
            required: true,
        },
        neighborName: {
            type: String,
        },
        selfName: {
            type: String,
        },
        neighborSide: {
            type: String, // 'left' | 'right'
            required: true,
        },
        side: {
            type: String, // 'left' | 'right'
            required: true,
        },
        viewParams: {
            type: Object,
            required: true,
        },
    },
    setup() {
        const {
            showSimilarParaBar,
        } = useSimilarParaScroll();

        const {
            enabledCompareWording,
            showWordingNotFoundBar,
        } = useCompareWording();

        const {
            isSourceInTextTab,
        } = useCompareUrlState();

        const {
            updateDualViewRef,
            clearDualViewRef,
        } = useDualViewStates();

        const {
            permissions,
        } = usePermission();

        const {
            getPatentBase,
        } = usePatentBasic();

        return {
            showSimilarParaBar,
            enabledCompareWording,
            showWordingNotFoundBar,
            isSourceInTextTab,
            clearDualViewRef,
            updateDualViewRef,
            permissions,
            getPatentBase,
        };
    },
    data() {
        return {
            patent: {},
            routeLoading: false,
            isFree: getIsFreeView(),
            isSpecialIp: _.get(window, 'zPREFACE.globalConfig.userInfo.isSpecialIp'),
        };
    },
    computed: {
        hasDensity() {
            return comparePluginsMapByName.get(this.selfName).enableDensity;
        },
        selfHandelPadding() {
            return comparePluginsMapByName.get(this.selfName).selfHandelPadding;
        },
        dualViewClassStatus() {
            return {
                'dual-aside-box--no-padding': this.selfHandelPadding,
                'dual-aside-box--no-density': !this.hasDensity,
                'dual-aside-box--has-catalog': this.selfName === 'desc',
            };
        },
        defaultTabs() {
            const filteredAclList = getFilteredCompareManifestList(this.side, this.$store)
                .map(mf => (
                    {
                        label: this.$t(mf.label),
                        key: mf.name,
                        hasAcl: !mf.aclCheckKey || (mf.aclCheckKey && this.permissions[mf.aclCheckKey]),
                    }
                ))
                .filter(item => item.hasAcl);
            return filteredAclList;
        },
        visibleTabs() {
            return this.defaultTabs.filter(item => item.key !== compareManifest.name);
        },
        isInComparePath() {
            return this.selfName === compareManifest.name;
        },
    },
    watch: {
        patentId: {
            handler(newId) {
                if (this.isInComparePath) return;
                this.changePatentInfo(newId);
                window.rvTrack('View Patent Compare View');
            },
            immediate: true,
        },
        selfName: {
            handler(newVal) {
                this.checkCurrentTabIsExist(newVal);
            },
            immediate: true,
        },
    },
    updated() {
        this.$nextTick(() => {
            this.updateDualViewRef({ side: this.side, ref: this.$refs.viewVm });
        });
    },
    destroyed() {
        this.clearDualViewRef({ side: this.side });
    },
    methods: {
        switchTabHandler(tab) {
            this.$router.push({
                name: this.$route.name,
                params: {
                    ...this.$route.params,
                    [this.side]: tab.key,
                },
                query: this.$route.query,
            });
        },
        resetTabsStatus() {
            const menuTabsScrollableReset = _.get(this.$refs, 'menuTabsScrollable.initScrollStatus');
            menuTabsScrollableReset && menuTabsScrollableReset();
        },
        resizeHandler() {
            this.resetTabsStatus();
        },
        checkCurrentTabIsExist(currentTab) {
            if (_.findIndex(this.defaultTabs, { key: currentTab }) < 0) { // 404逻辑
                console.error(`404 Unknown route ${currentTab}, redirect`);
                this.$router.replace({
                    params: {
                        [this.side]: this.defaultTabs[0].key,
                        [this.neighborSide]: this.defaultTabs[0].key,
                    },
                    query: this.$route.query,
                });
            }
            this.$nextTick(() => {
                this.updateDualViewRef({ side: this.side, ref: this.$refs.viewVm });
            });
        },
        async changePatentInfo(patentId) {
            if (this.isInComparePath) return;
            this.routeLoading = true;
            this.patent = await this.getPatentBase(patentId);
            this.routeLoading = false;
        },
    },
};
</script>

<style lang="scss">
.ps-side-view {
    position: relative;
    width: 50%;
    .ps-side-view__tab {
        background-color: $bg-normal;
    }
    .density-bar{
        bottom: 0;
        right: 18px;
    }
    &.view-left {
        border-right: 1px solid #DFE1E6;
    }
}
.dual-aside-box{
    overflow: auto;
    max-height: 100%;
    padding: 16px 40px 16px 16px;
    position: relative;
    &--no-density {
        padding: 16px;
    }
    &--no-padding {
        padding: 0;
    }
    &--has-catalog {
        padding-right: 56px;
    }
}
</style>
